// ---------------------------------------------------------------------------
// Conversion funnel visualization — vertical funnel with stage counts
// ---------------------------------------------------------------------------

import { useFormatters } from '../hooks/useFormatters';

interface FunnelStage {
  stage: string;
  count: number;
  dropoffRate: number;
  avgTimeInStage: number;
}

interface ConversionFunnelProps {
  stages: FunnelStage[];
}

const STAGE_LABELS: Record<string, string> = {
  started: 'Started',
  documents: 'Documents Submitted',
  biometrics: 'Biometrics Captured',
  screening: 'Screening',
  decision: 'Decision',
  complete: 'Complete',
};

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
  return `${(ms / 3600000).toFixed(1)}h`;
}

export function ConversionFunnel({ stages }: ConversionFunnelProps) {
  const f = useFormatters();
  const maxCount = stages.length > 0 ? Math.max(...stages.map((s) => s.count), 1) : 1;

  return (
    <div className="bg-surface-raised rounded-sm border border-hairline p-6">
      <h3 className="font-mono text-[0.6875rem] font-medium text-ink-muted uppercase tracking-[0.08em] mb-6">
        Conversion Funnel
      </h3>

      <div className="space-y-3">
        {stages.map((stage, i) => {
          const widthPct = Math.max((stage.count / maxCount) * 100, 8);
          const label = STAGE_LABELS[stage.stage] ?? stage.stage;

          return (
            <div key={stage.stage}>
              {/* Stage bar */}
              <div className="flex items-center gap-3">
                <div className="w-28 text-end">
                  <span className="text-sm font-medium text-ink">{label}</span>
                </div>
                <div className="flex-1 relative">
                  <div
                    className="h-8 rounded-sm bg-primary flex items-center justify-end pe-3 transition-all"
                    style={{ width: `${widthPct}%` }}
                  >
                    <span className="text-xs font-medium text-white font-mono tabular-nums">
                      {f.number(stage.count)}
                    </span>
                  </div>
                </div>
                <div className="w-20 text-end">
                  {stage.avgTimeInStage > 0 && (
                    <span className="text-xs text-ink-muted font-mono">
                      {formatDuration(stage.avgTimeInStage)}
                    </span>
                  )}
                </div>
              </div>

              {/* Drop-off indicator between stages */}
              {i < stages.length - 1 && stage.dropoffRate > 0 && (
                <div className="flex items-center gap-3 mt-1 mb-1">
                  <div className="w-28" />
                  <div className="flex-1 flex items-center gap-2 ps-2">
                    <svg
                      className="w-3 h-3 text-danger flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      />
                    </svg>
                    <span className="text-xs text-danger font-medium">
                      <span className="font-mono tabular-nums">{stage.dropoffRate.toFixed(1)}%</span> drop-off
                    </span>
                  </div>
                  <div className="w-20" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {stages.length === 0 && (
        <p className="text-sm text-ink-muted text-center py-8">
          No funnel data available for this period.
        </p>
      )}
    </div>
  );
}
